import React, { useState, Component } from 'react';
import { Activity, ShieldCheck, Sliders, Table2, RefreshCw, AlertTriangle, Camera, GitCompare, GraduationCap } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Button } from '../../../components/ui/Button';
import { RulesTab, GovernanceSettingsTab, useGovernanceData, OverviewDashboard, QuestionsTab, CompareSnapshotsTab } from '../content/governance';
import { AssessmentPoolHealthTab } from '../content/governance/tabs/AssessmentPoolHealthTab';
import { SaveSnapshotModal } from '../content/governance/tabs/SaveSnapshotModal';
import { isVisualPolicyDegraded } from '../../../governance/pipeline/policyEngine';
import { createSnapshot, saveSnapshot } from '../../../governance/pipeline/snapshots';
// ─── Types & Constants ────────────────────────────────────────────────────────
type TabId = 'overzicht' | 'vragen' | 'vergelijken' | 'toetsen' | 'regels' | 'instellingen';
const TABS = [{
  id: 'overzicht',
  label: 'Overzicht',
  icon: <Activity className="w-4 h-4" data-id="element-6883" />
}, {
  id: 'vragen',
  label: 'Vragen',
  icon: <Table2 className="w-4 h-4" data-id="element-6884" />
}, {
  id: 'vergelijken',
  label: 'Vergelijken',
  icon: <GitCompare className="w-4 h-4" data-id="element-6885" />
}, {
  id: 'toetsen',
  label: 'Toetsen',
  icon: <GraduationCap className="w-4 h-4" data-id="element-6886" />
}, {
  id: 'regels',
  label: 'Regels',
  icon: <ShieldCheck className="w-4 h-4" data-id="element-6887" />
}, {
  id: 'instellingen',
  label: 'Instellingen',
  icon: <Sliders className="w-4 h-4" data-id="element-6888" />
}] as const;
const TAB_DESCRIPTIONS: Record<TabId, string> = {
  overzicht: 'Systeemgezondheid, top blockers en module status op een oogopslag.',
  vragen: 'Eén centrale tabel met alle vragen, validatieresultaten, quality scores en seed issues.',
  vergelijken: 'Vergelijk twee pipeline runs om regressies of verbeteringen te spotten.',
  toetsen: 'Toets-feasibility per groep × module: kan de blueprint-generator een volledige Cito-style toets opleveren?',
  regels: 'Alle validatieregels, regelboeken en drempelwaarden — de complete governance-referentie.',
  instellingen: 'Configureer kwaliteitspoorten, pipeline-fasen en beleidsregels.'
};
// ─── Health Status ────────────────────────────────────────────────────────────
const HEALTH_CONFIG = {
  pending: {
    label: 'Niet uitgevoerd',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    dot: 'bg-gray-400'
  },
  ok: {
    label: 'Gezond',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500'
  },
  warning: {
    label: 'Aandacht vereist',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500'
  },
  error: {
    label: 'Kritieke fouten',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500'
  }
};
function getHealthStatus(fail: number, review: number, isRunning: boolean) {
  if (isRunning) return 'pending';
  if (fail > 0) return 'error';
  if (review > 0) return 'warning';
  return 'ok';
}
// ─── Main Page Component ──────────────────────────────────────────────────────
export function GovernancePage() {
  const [activeTab, setActiveTab] = useState<TabId>('overzicht');
  const [questionFilter, setQuestionFilter] = useState<string | undefined>(undefined);
  const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
  const [snapshotRefreshTrigger, setSnapshotRefreshTrigger] = useState(0);
  // R8 (Sprint 5): surface degraded visual-policy state so admins know that
  // visual answer-leakage checks may be running on the empty default policy.
  const visualPolicyDegraded = isVisualPolicyDegraded();
  const {
    rows,
    stats,
    systemStats,
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    topBlockers,
    familyTriage,
    assessmentPoolHealth,
    pipelineIssueCounts,
    isRunning,
    lastRun,
    batchResult,
    revalidate,
    shadowImpactReport
  } = useGovernanceData();
  const overallHealth = getHealthStatus(stats.fail, stats.review, isRunning);
  const hcfg = HEALTH_CONFIG[overallHealth];
  const handleNavigateToQuestion = (filter?: string) => {
    setQuestionFilter(filter);
    setActiveTab('vragen');
  };
  const handleSaveSnapshot = (label: string, note: string) => {
    if (!batchResult) return;
    const snap = createSnapshot(batchResult, {
      label: label || undefined,
      note: note || undefined
    });
    const success = saveSnapshot(snap);
    if (success) {
      setSnapshotRefreshTrigger(prev => prev + 1);
      // In a real app we'd use a toast here
      alert('Snapshot succesvol opgeslagen!');
    } else {
      alert('Opslaan mislukt — localStorage vol?');
    }
  };
  return <PageLayout title="Governance & Kwaliteit" subtitle="Kwaliteitsbeheer, validatie en analyse van het vragenbestand" actions={<div className="flex items-center gap-3" data-id="element-6890">
          {/* Health Status Indicator */}
          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${hcfg.border} ${hcfg.bg}`} data-id="element-6891">
            <div className={`w-2 h-2 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-6892" />
            <span className={`text-xs font-medium ${hcfg.color}`} data-id="element-6893">
              {hcfg.label}
            </span>
          </div>

          <Button variant="secondary" onClick={() => setIsSnapshotModalOpen(true)} disabled={isRunning || !batchResult} data-id="element-6894">
            <Camera className="w-4 h-4 mr-2" data-id="element-6895" />
            Snapshot opslaan
          </Button>

          {/* Re-validate Button */}
          <Button variant="primary" onClick={revalidate} disabled={isRunning} data-id="element-6896">
            {isRunning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-6897" /> : <RefreshCw className="w-4 h-4 mr-2" data-id="element-6898" />}
            {isRunning ? 'Valideren...' : 'Hervalideer'}
          </Button>

          <ConnectionBadge status="live" data-id="element-6899" />
        </div>} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Governance & Kwaliteit'
  }]} data-id="element-6889">
      {/* R8: Visual-policy degraded banner */}
      {visualPolicyDegraded && <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 mb-4" data-id="element-6900">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-6901" />
          <div className="text-sm" data-id="element-6902">
            <p className="font-semibold text-red-800" data-id="element-6903">
              Visuele policy gedegradeerd
            </p>
            <p className="text-red-700 mt-1" data-id="element-6904">
              De visual policy kon niet uit de seeds worden geladen (build
              mislukte 3×). Visual-stage answer-leakage checks draaien tegen de
              lege default policy — visual-issues kunnen onvolledig zijn. Een
              app-restart kan dit oplossen.
            </p>
          </div>
        </div>}

      {/* Health Banner (Mobile) */}
      <div className={`sm:hidden flex items-center justify-between gap-3 rounded-xl border ${hcfg.border} ${hcfg.bg} p-3 mb-3`} data-id="element-6905">
        <div className="flex items-center gap-2" data-id="element-6906">
          <div className={`w-2.5 h-2.5 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-6907" />
          <span className={`text-sm font-medium ${hcfg.color}`} data-id="element-6908">
            {hcfg.label}
          </span>
        </div>
        {lastRun && <span className="text-xs text-gray-500" data-id="element-6909">{lastRun}</span>}
      </div>

      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={id => {
      setActiveTab(id as TabId);
      if (id !== 'vragen') setQuestionFilter(undefined);
    }} data-id="element-6910" />

      <p className="text-sm text-gray-500 mb-5" data-id="element-6911">
        {TAB_DESCRIPTIONS[activeTab]}
      </p>

      <div data-id="element-6912">
        {activeTab === 'overzicht' && <OverviewDashboard rows={rows} stats={stats} systemStats={systemStats} moduleHealth={moduleHealth} familyHealthByModule={familyHealthByModule} questionTypeDistribution={questionTypeDistribution} visualTypeDistribution={visualTypeDistribution} topBlockers={topBlockers} familyTriage={familyTriage} pipelineIssueCounts={pipelineIssueCounts} shadowImpactReport={shadowImpactReport} isRunning={isRunning} onNavigateToQuestion={handleNavigateToQuestion} data-id="element-6913" />}
        {activeTab === 'vragen' && <QuestionsTab rows={rows} batchResult={batchResult} isRunning={isRunning} initialFilter={questionFilter} data-id="element-6914" />}
        {activeTab === 'vergelijken' && <CompareSnapshotsTab refreshTrigger={snapshotRefreshTrigger} data-id="element-6915" />}
        {activeTab === 'toetsen' && <AssessmentPoolHealthTab report={assessmentPoolHealth} isRunning={isRunning} onSelectQuestion={handleNavigateToQuestion} data-id="element-6916" />}
        {activeTab === 'regels' && <RulesTab data-id="element-6917" />}
        {activeTab === 'instellingen' && <GovernanceSettingsTab data-id="element-6918" />}
      </div>

      <SaveSnapshotModal isOpen={isSnapshotModalOpen} onClose={() => setIsSnapshotModalOpen(false)} onSave={handleSaveSnapshot} data-id="element-6919" />
    </PageLayout>;
}